import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import IconButton from '@material-ui/core/IconButton';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import AppInput from '@components/Core/inputs/AppInput/AppInput';
import './questionAnswerFields.less';

const MIN_QA_PAIRS = 1;
const MAX_QA_PAIRS = 3;

const QuestionAnswerFields = ({challenges, onChallengesChange}) => {
    const intl = useIntl();
    const [touchedFields, setTouchedFields] = useState({});

    const handleQuestionChange = (index, value) => {
        const updatedChallenges = [...challenges];
        updatedChallenges[index] = {
            ...updatedChallenges[index],
            question: value
        };
        onChallengesChange(updatedChallenges);
    };

    const handleAnswerChange = (index, value) => {
        const updatedChallenges = [...challenges];
        updatedChallenges[index] = {
            ...updatedChallenges[index],
            answer: value
        };
        onChallengesChange(updatedChallenges);
    };

    const handleAddPair = () => {
        if (challenges.length < MAX_QA_PAIRS) {
            onChallengesChange([
                ...challenges,
                {question: '', answer: '', maskInput: false}
            ]);
        }
    };

    const handleRemovePair = (index) => {
        if (challenges.length > MIN_QA_PAIRS) {
            const updatedChallenges = challenges.filter((_, i) => i !== index);
            onChallengesChange(updatedChallenges);
        }
    };

    const handleFieldBlur = (index, fieldType) => {
        setTouchedFields({
            ...touchedFields,
            [`${index}-${fieldType}`]: true
        });
    };

    const getFieldError = (index, fieldType) => {
        const fieldKey = `${index}-${fieldType}`;
        if (!touchedFields[fieldKey]) {
            return '';
        }

        const challenge = challenges[index];
        if (!challenge) {
            return '';
        }

        if (fieldType === 'question' && (!challenge.question || challenge.question.trim().length === 0)) {
            return intl.formatMessage({
                id: 'oss.components.ReassignAuth.qa.questionRequired',
                defaultMessage: 'Question is required'
            });
        }

        if (fieldType === 'answer' && (!challenge.answer || challenge.answer.trim().length === 0)) {
            return intl.formatMessage({
                id: 'oss.components.ReassignAuth.qa.answerRequired',
                defaultMessage: 'Answer is required'
            });
        }

        return '';
    };

    const getCanAddPair = () => challenges.length < MAX_QA_PAIRS;
    const getCanRemovePair = () => challenges.length > MIN_QA_PAIRS;

    return (
        <div className="question-answer-fields" data-wdio="test-qa-fields">
            {challenges.map((challenge, index) => {
                const questionError = getFieldError(index, 'question');
                const answerError = getFieldError(index, 'answer');

                return (
                    <div key={`qa-pair-${index}`} className="question-answer-fields__pair">
                        <div className="question-answer-fields__header">
                            <span className="question-answer-fields__pair-label">
                                {intl.formatMessage(
                                    {
                                        id: 'oss.components.ReassignAuth.qa.pairLabel',
                                        defaultMessage: 'Question & Answer {number}'
                                    },
                                    {number: index + 1}
                                )}
                            </span>
                            {getCanRemovePair() && (
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        id: 'oss.components.ReassignAuth.qa.removePair',
                                        defaultMessage: 'Remove question and answer pair'
                                    })}
                                    onClick={() => handleRemovePair(index)}
                                    size="small"
                                    className="question-answer-fields__remove-btn"
                                    data-wdio={`test-qa-remove-${index}`}
                                >
                                    <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                        <AppInput
                            label={intl.formatMessage({
                                id: 'oss.components.ReassignAuth.qa.question',
                                defaultMessage: 'Security Question'
                            })}
                            value={challenge.question}
                            onChange={(e) => handleQuestionChange(index, e.target.value)}
                            onBlur={() => handleFieldBlur(index, 'question')}
                            error={!!questionError}
                            helperText={questionError}
                            required
                            dataWdio={`test-qa-question-${index}`}
                            placeholder={intl.formatMessage({
                                id: 'oss.components.ReassignAuth.qa.questionPlaceholder',
                                defaultMessage: 'Enter a security question'
                            })}
                            className="question-answer-fields__input"
                        />
                        <AppInput
                            label={intl.formatMessage({
                                id: 'oss.components.ReassignAuth.qa.answer',
                                defaultMessage: 'Answer'
                            })}
                            value={challenge.answer}
                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                            onBlur={() => handleFieldBlur(index, 'answer')}
                            error={!!answerError}
                            helperText={answerError}
                            required
                            dataWdio={`test-qa-answer-${index}`}
                            placeholder={intl.formatMessage({
                                id: 'oss.components.ReassignAuth.qa.answerPlaceholder',
                                defaultMessage: 'Enter the answer'
                            })}
                            className="question-answer-fields__input"
                        />
                    </div>
                );
            })}
            {getCanAddPair() && (
                <button
                    type="button"
                    onClick={handleAddPair}
                    className="question-answer-fields__add-btn"
                    data-wdio="test-qa-add-pair"
                >
                    <AddCircleOutlineIcon fontSize="small" className="question-answer-fields__add-icon" />
                    {intl.formatMessage({
                        id: 'oss.components.ReassignAuth.qa.addPair',
                        defaultMessage: 'Add another question'
                    })}
                </button>
            )}
            <div className="question-answer-fields__helper-text">
                {intl.formatMessage(
                    {
                        id: 'oss.components.ReassignAuth.qa.helperText',
                        defaultMessage: 'You can add up to {max} question and answer pairs. Minimum {min} required.'
                    },
                    {max: MAX_QA_PAIRS, min: MIN_QA_PAIRS}
                )}
            </div>
        </div>
    );
};

QuestionAnswerFields.propTypes = {
    challenges: PropTypes.arrayOf(
        PropTypes.shape({
            question: PropTypes.string.isRequired,
            answer: PropTypes.string.isRequired,
            maskInput: PropTypes.bool
        })
    ).isRequired,
    onChallengesChange: PropTypes.func.isRequired
};

export default QuestionAnswerFields;
